import React, { useState, useEffect } from "react";
import { Box, Container, Heading, Text, Grid, VStack } from "@chakra-ui/react";
import {
  getFemaleRatioData,
  formatFemaleRatioData,
} from "../../services/femaleRatioService";
import {
  getAverageAgeData,
  formatAverageAgeData,
} from "../../services/averageAgeService";
import {
  getServiceUptimeData,
  formatServiceUptimeData,
} from "../../services/serviceUptimeService";

const OurImpactSection = () => {
  const [femaleRatioData, setFemaleRatioData] = useState(null);
  const [averageAgeData, setAverageAgeData] = useState(null);
  const [serviceUptimeData, setServiceUptimeData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch all dynamic data on component mount
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        // Fetch female ratio data
        const femaleRatioResult = await getFemaleRatioData();
        if (femaleRatioResult.success && femaleRatioResult.data) {
          const formatted = formatFemaleRatioData(femaleRatioResult.data);
          setFemaleRatioData(formatted);
        }

        // Fetch average age data
        const avgAgeResult = await getAverageAgeData();
        if (avgAgeResult.success && avgAgeResult.data) {
          const formatted = formatAverageAgeData(avgAgeResult.data);
          setAverageAgeData(formatted);
        }

        // Fetch service uptime data
        const uptimeResult = await getServiceUptimeData();
        if (uptimeResult.success && uptimeResult.data) {
          const formatted = formatServiceUptimeData(uptimeResult.data);
          setServiceUptimeData(formatted);
        }
      } catch (error) {
        console.error("Error fetching impact data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  // Define impact data with dynamic values from APIs
  const impactData = [
    {
      id: 1,
      title: "Years in technology leadership",
      subtitle: "Serving Bangladesh’s Digital Transformation Since 1998",
      value: "27+",
      icon: <img src="svg/Years.svg" alt="user icon" />,
      bgColor: "green.50",
    },
    {
      id: 2,
      title: "Average age of team members",
      subtitle: "Young, Dynamic, and Innovation-Driven",
      value: loading ? "..." : averageAgeData?.age || "",
      icon: <img src="svg/Average_Age.svg" alt="user icon" />,
      bgColor: "purple.50",
    },

    {
      id: 3,
      title: "Women in the Workforce",
      subtitle: "Driving Diversity & Inclusion in Tech",
      value: loading ? "..." : femaleRatioData?.percentage || "",
      icon: <img src="svg/Women_in_Engineering.svg" alt="user icon" />,
      bgColor: "purple.50",
    },
    {
      id: 4,
      title: "Client & Project delivered",
      subtitle:
        "Trusted by Government, Development Agencies, and Industry Leaders",
      value: "300+",
      icon: <img src="svg/Client_Project_Delivered.svg" alt="user icon" />,
      bgColor: "yellow.50",
    },

    {
      id: 5,
      title: "Platform uptime",
      subtitle: "Ensuring Reliability for Mission-Critical Systems",
      smallText: "(Past 30 Days)",
      value: loading ? "..." : serviceUptimeData?.uptime || "",
      icon: <img src="svg/Platform_Uptime.svg" alt="user icon" />,
      bgColor: "yellow.50",
    },
  ];

  return (
    <Box py={{ base: "30px", md: "40px" }} pb={{ base: "15px", md: "20px" }}>
      <Container maxW="1200px">
        <VStack spacing={8} textAlign="center" mb={12}>
          <Heading
            fontSize={{ base: "2xl", md: "4xl" }}
            fontWeight="700"
            color="gray.800"
            lineHeight="1.2"
          >
            Our{" "}
            <Text as="span" color="#000B8C">
              Impact
            </Text>
          </Heading>
          <Text
            fontSize={{ base: "md", md: "lg" }}
            color="gray.600"
            lineHeight="1.6"
            maxW="7xl"
          >
            We believe in providing quality services, which means any website or
            application curated by us will be clean-coded, speed optimized,
            all-device tested and SEO friendly. Our experienced professionals
            ensure the creation of innovative web solutions to meet your goals.
          </Text>
        </VStack>

        <VStack spacing={5}>
          {/* First Row - 3 Cards */}
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
            gap={5}
            w="full"
          >
            {impactData.slice(0, 3).map((item) => (
              <Box
                key={item.id}
                bg={item.bgColor}
                borderRadius="12px"
                border="1px solid"
                borderColor="gray.100"
                transition="all 0.3s ease"
                _hover={{
                  transform: "translateY(-5px)",
                  boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                }}
                p={6}
                minH="120px"
              >
                {/* Two Column Layout */}
                <Grid
                  templateColumns="1fr 1px auto"
                  h="full"
                  gap={4}
                  alignItems="stretch"
                >
                  {/* Left Column - Title and Description */}
                  <VStack align="start" justify="space-between" spacing={3}>
                    <VStack align="start" spacing={2}>
                      <Text
                        fontSize="lg"
                        fontWeight="600"
                        color="gray.800"
                        lineHeight="1.3"
                      >
                        {item.title}
                      </Text>
                      <Text fontSize="sm" color="gray.600" lineHeight="1.4">
                        {item.subtitle}
                      </Text>
                      {item.smallText && (
                        <Text
                          fontSize="md"
                          color="gray.500"
                          lineHeight="1.2"
                          fontStyle="italic"
                        >
                          {item.smallText}
                        </Text>
                      )}
                    </VStack>
                  </VStack>

                  {/* Vertical Border */}
                  <Box bg="gray.200" w="2px" h="full" />

                  {/* Right Column - Icon and Value */}
                  <VStack
                    justify="space-between"
                    align="flex-end"
                    spacing={4}
                    minW="80px"
                  >
                    {/* Icon at top */}
                    <Box
                      w="50px"
                      h="50px"
                      borderRadius="10px"
                      bg="blue.50"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      {item.icon === "svg" ? (
                        <svg
                          width="41"
                          height="40"
                          viewBox="0 0 41 40"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M14.3874 9.78635C14.2724 10.1312 14.3856 10.5008 14.6754 10.728L17.2562 12.7505L16.3876 15.8658C16.2901 16.2156 16.4214 16.5794 16.7224 16.7929C17.0235 17.0058 17.4187 17.0152 17.7299 16.8167L20.5002 15.0485L23.2706 16.8168C23.42 16.9121 23.5886 16.9594 23.757 16.9594C23.9395 16.9594 24.1216 16.9038 24.2779 16.7929C24.5791 16.5796 24.7105 16.2156 24.6128 15.8658L23.7441 12.7506L26.3248 10.7281C26.6148 10.501 26.7279 10.1313 26.6129 9.78635C26.498 9.44129 26.1837 9.20706 25.8122 9.18954L22.505 9.03232L21.3297 6.01425C21.1978 5.67521 20.8721 5.45606 20.5001 5.45606C20.1282 5.45606 19.8025 5.67513 19.6706 6.01417L18.4952 9.0324L15.1883 9.18954C14.8167 9.20706 14.5023 9.44129 14.3874 9.78635ZM18.7932 10.2384C19.1457 10.2216 19.4544 10.0029 19.5794 9.68131L20.5 7.31732L21.4206 9.68154C21.546 10.0031 21.8547 10.2216 22.2069 10.2384L24.7972 10.3615L22.7757 11.9457C22.5006 12.1613 22.3828 12.5151 22.4754 12.8469L23.1558 15.287L20.9858 13.902C20.8382 13.8078 20.6691 13.7608 20.5 13.7608C20.3308 13.7608 20.1615 13.8079 20.0139 13.9021L17.8441 15.287L18.5245 12.8471C18.6171 12.515 18.4992 12.1612 18.2241 11.9457L16.2026 10.3616L18.7932 10.2384ZM19.8751 3.47453V1.23828C19.8751 0.901753 20.1549 0.628906 20.5 0.628906C20.8452 0.628906 21.125 0.901753 21.125 1.23828V3.47453C21.125 3.81106 20.8452 4.0839 20.5 4.0839C20.1549 4.0839 19.8751 3.81106 19.8751 3.47453ZM23.6811 7.08492C23.4502 6.83485 23.4709 6.44949 23.7274 6.22433L26.2832 3.98046C26.5397 3.75522 26.9349 3.77541 27.1659 4.02556C27.3968 4.27563 27.3761 4.66098 27.1197 4.88614L24.5638 7.13001C24.4443 7.2349 24.2948 7.28654 24.1457 7.28654C23.9749 7.28654 23.8046 7.2186 23.6811 7.08492ZM13.8342 4.02556C14.0651 3.77541 14.4603 3.75522 14.7169 3.98046L17.2728 6.22433C17.5292 6.44949 17.5499 6.83485 17.319 7.08492C17.1956 7.21852 17.0253 7.28654 16.8543 7.28654C16.7054 7.28654 16.5558 7.2349 16.4363 7.13001L13.8804 4.88614C13.6239 4.66098 13.6032 4.27563 13.8342 4.02556ZM28.5181 10.728L31.0989 12.7505L30.2303 15.8658C30.1328 16.2156 30.2641 16.5794 30.5651 16.7929C30.866 17.0058 31.2613 17.0151 31.5726 16.8167L34.3429 15.0485L37.1133 16.8168C37.2627 16.9121 37.4314 16.9594 37.5997 16.9594C37.7822 16.9594 37.9643 16.9038 38.1207 16.7929C38.4217 16.5798 38.5531 16.2159 38.4555 15.8658L37.5868 12.7506L40.1675 10.7281C40.4575 10.5011 40.5706 10.1314 40.4556 9.78635C40.3407 9.44129 40.0263 9.20706 39.6549 9.18954L36.3477 9.03232L35.1725 6.01425C35.0406 5.67521 34.7149 5.45606 34.3429 5.45606C33.9709 5.45606 33.6452 5.67513 33.5133 6.01417L32.3381 9.03232L29.0311 9.18947C28.6594 9.20699 28.345 9.44129 28.2301 9.78635C28.1151 10.1313 28.2282 10.501 28.5181 10.728ZM32.6359 10.2384C32.9884 10.2216 33.2972 10.0029 33.4223 9.68123L34.3428 7.31724L35.2633 9.68116C35.3885 10.0029 35.6973 10.2216 36.0497 10.2383L38.64 10.3614L36.6187 11.9454C36.3435 12.161 36.2255 12.5149 36.3182 12.8469L36.9986 15.2869L34.8286 13.9019C34.681 13.8078 34.5118 13.7607 34.3427 13.7607C34.1735 13.7607 34.0043 13.8078 33.8567 13.902L31.6869 15.2869L32.3673 12.847C32.4598 12.5151 32.342 12.1613 32.0669 11.9456L30.0454 10.3614L32.6359 10.2384ZM0.832516 10.728L3.41332 12.7505L2.54466 15.8658C2.44717 16.2156 2.57849 16.5794 2.8795 16.7929C3.18059 17.0058 3.57581 17.0152 3.88698 16.8167L6.65731 15.0485L9.42771 16.8168C9.57708 16.9121 9.74575 16.9594 9.91411 16.9594C10.0966 16.9594 10.2787 16.9038 10.435 16.7929C10.736 16.5798 10.8674 16.2159 10.7699 15.8658L9.90122 12.7506L12.4819 10.7281C12.7717 10.5012 12.8849 10.1316 12.77 9.78597C12.6548 9.44114 12.3405 9.20699 11.9693 9.18947L8.6621 9.03225L7.4869 6.01425C7.35495 5.67521 7.02925 5.45606 6.65731 5.45606C6.28528 5.45606 5.95959 5.67513 5.82764 6.01417L4.65243 9.03232L1.34547 9.18947C0.973841 9.20699 0.659473 9.44129 0.544632 9.78597C0.4294 10.1311 0.542444 10.5009 0.832516 10.728ZM4.95023 10.2384C5.3028 10.2216 5.61155 10.0029 5.7367 9.68123L6.65723 7.31724L7.57768 9.68116C7.70291 10.0029 8.01165 10.2216 8.36406 10.2383L10.9544 10.3614L8.93311 11.9454C8.65789 12.161 8.53992 12.5149 8.63257 12.8469L9.31295 15.2869L7.143 13.9019C6.99535 13.8078 6.82621 13.7607 6.65707 13.7607C6.48786 13.7607 6.31864 13.8078 6.17107 13.902L4.00127 15.2869L4.68165 12.847C4.77422 12.5151 4.65641 12.1613 4.38126 11.9456L2.35982 10.3614L4.95023 10.2384ZM40.4012 32.9968C39.9615 31.6121 39.0608 30.3689 37.865 29.4958C37.2925 29.0778 36.669 28.7523 36.0114 28.5218C36.9232 27.824 37.5107 26.7409 37.5107 25.5257C37.5107 23.426 35.7586 21.7177 33.6051 21.7177C31.4516 21.7177 29.6995 23.426 29.6995 25.5257C29.6995 26.7394 30.2856 27.8215 31.1956 28.5193C29.976 28.9438 28.8955 29.6895 28.0681 30.6943C27.6814 30.1669 27.2354 29.6769 26.7339 29.2347C25.7234 28.3438 24.5376 27.6894 23.2605 27.3027C24.6148 26.4274 25.5101 24.9285 25.5101 23.2291C25.5101 20.5356 23.2626 18.3443 20.5001 18.3443C17.7377 18.3443 15.4902 20.5356 15.4902 23.2291C15.4902 24.9285 16.3854 26.4274 17.7396 27.3027C16.4625 27.6894 15.2766 28.3438 14.2662 29.2347C13.7647 29.6769 13.3186 30.1669 12.932 30.6943C12.1047 29.6893 11.0242 28.9436 9.80458 28.5191C10.7145 27.8213 11.3006 26.7392 11.3006 25.5255C11.3006 23.4257 9.54857 21.7175 7.3951 21.7175C5.24156 21.7175 3.48949 23.4257 3.48949 25.5255C3.48949 26.7406 4.07697 27.8238 4.98867 28.5215C4.33103 28.752 3.70761 29.0776 3.13504 29.4956C1.93921 30.3686 1.03853 31.6119 0.598771 32.9965C0.395573 33.636 0.508148 34.2851 0.915717 34.8242C1.32321 35.3632 1.92585 35.66 2.61255 35.66H11.2524V36.7598C11.2524 38.3418 12.5725 39.6289 14.1951 39.6289H26.805C28.4275 39.6289 29.7476 38.3418 29.7477 36.7598V35.6603H38.3875C39.0743 35.6603 39.6769 35.3634 40.0844 34.8245C40.4918 34.2854 40.6044 33.6363 40.4012 32.9968ZM33.6051 22.9365C35.0694 22.9365 36.2607 24.098 36.2607 25.5257C36.2607 26.9535 35.0694 28.115 33.6051 28.115C32.1408 28.115 30.9495 26.9535 30.9495 25.5257C30.9495 24.098 32.1408 22.9365 33.6051 22.9365ZM16.7401 23.2291C16.7401 21.2076 18.4269 19.5631 20.5001 19.5631C22.5734 19.5631 24.2601 21.2076 24.2601 23.2291C24.2601 25.2499 22.5744 26.894 20.5021 26.8951H20.5H20.498C18.4257 26.8939 16.7401 25.2498 16.7401 23.2291ZM7.3951 22.9362C8.85937 22.9362 10.0507 24.0977 10.0507 25.5254C10.0507 26.9532 8.85937 28.1147 7.3951 28.1147C5.93076 28.1147 4.73946 26.9532 4.73946 25.5254C4.73946 24.0977 5.93076 22.9362 7.3951 22.9362ZM11.3336 34.4412H2.61263C2.32498 34.4412 2.09264 34.3267 1.92194 34.101C1.75117 33.8752 1.70781 33.6249 1.79288 33.357C2.55724 30.9504 4.80868 29.3335 7.3951 29.3335C9.35154 29.3335 11.1338 30.2551 12.2299 31.8154C11.7966 32.6362 11.4931 33.5209 11.3336 34.4412ZM28.4977 36.7597C28.4977 37.6697 27.7383 38.4101 26.805 38.4101H14.1951C13.2618 38.4101 12.5024 37.6697 12.5024 36.7597V35.0837C12.9549 31.1084 16.3864 28.1148 20.498 28.1138L20.5001 28.1139L20.5021 28.1138C24.6137 28.1149 28.0451 31.1084 28.4977 35.0837V36.7597ZM39.0782 34.1014C38.9075 34.3271 38.6752 34.4415 38.3875 34.4415H29.6666C29.5071 33.5212 29.2036 32.6364 28.7703 31.8155C29.8665 30.2554 31.6487 29.3338 33.6051 29.3338C36.1915 29.3338 38.4429 30.9507 39.2073 33.3574C39.2923 33.6251 39.2489 33.8754 39.0782 34.1014Z"
                            fill="#000B8C"
                          />
                        </svg>
                      ) : (
                        <Text fontSize="24px" color="#000B8C">
                          {item.icon}
                        </Text>
                      )}
                    </Box>

                    {/* Value at bottom */}
                    <Text
                      fontSize={{ base: "3xl", md: "4xl" }}
                      fontWeight="700"
                      color="#000B8C"
                      lineHeight="1"
                    >
                      {item.value}
                    </Text>
                  </VStack>
                </Grid>
              </Box>
            ))}
          </Grid>

          {/* Second Row - 2 Cards */}
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
            gap={5}
            w="full"
            maxW="6xl"
          >
            {impactData.slice(3, 5).map((item) => (
              <Box
                key={item.id}
                bg={item.bgColor}
                borderRadius="12px"
                border="1px solid"
                borderColor="gray.100"
                transition="all 0.3s ease"
                _hover={{
                  transform: "translateY(-5px)",
                  boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                }}
                p={6}
                minH="100px"
              >
                {/* Two Column Layout */}
                <Grid
                  templateColumns="1fr 1px auto"
                  h="full"
                  gap={4}
                  alignItems="stretch"
                >
                  {/* Left Column - Title and Description */}
                  <VStack align="start" justify="space-between" spacing={3}>
                    <VStack align="start" spacing={2}>
                      <Text
                        fontSize="lg"
                        fontWeight="600"
                        color="gray.800"
                        lineHeight="1.3"
                      >
                        {item.title}
                      </Text>
                      <Text fontSize="sm" color="gray.600" lineHeight="1.4">
                        {item.subtitle}
                      </Text>
                      {item.smallText && (
                        <Text
                          fontSize="sm"
                          color="gray.500"
                          lineHeight="1.2"
                          fontStyle="italic"
                        >
                          {item.smallText}
                        </Text>
                      )}
                    </VStack>
                  </VStack>

                  {/* Vertical Border */}
                  <Box bg="gray.200" w="2px" h="full" />

                  {/* Right Column - Icon and Value */}
                  <VStack
                    justify="space-between"
                    align="flex-end"
                    spacing={4}
                    minW="80px"
                  >
                    {/* Icon at top */}
                    <Box
                      w="50px"
                      h="50px"
                      borderRadius="10px"
                      bg="blue.50"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Text fontSize="24px" color="#000B8C">
                        {item.icon}
                      </Text>
                    </Box>

                    {/* Value at bottom */}
                    <Text
                      fontSize={{ base: "3xl", md: "4xl" }}
                      fontWeight="700"
                      color="#000B8C"
                      lineHeight="1"
                    >
                      {item.value}
                    </Text>
                  </VStack>
                </Grid>
              </Box>
            ))}
          </Grid>
        </VStack>
      </Container>
    </Box>
  );
};

export default OurImpactSection;
